// Needed for the `tw` styling below: without it the pop-up was drawn with
// no styling at all (Steven's hand test of 5 Oct 2026: too small, title cut).
import 'twin.macro';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { fireEvent } from 'utils/eventHelper';
import { SaveNowMoot } from 'hooks/useAutoSave';

import Button from 'components/button/Button';
import { Modal } from 'components/page/app-page/Modal';
import { Title3 } from 'components/typography/Typography';

interface PendingSaveContextType {
  /**
   * A screen says whether it has work that is not stored. On one of the four
   * screens that store nothing while a required field is empty (D34), it
   * also says WHY: the leave question then tells the person the screen goes
   * back to how it was last saved (D35).
   */
  setPending: (pending: boolean, notStoredBecause?: string) => void;
  /**
   * A screen lends the box a way to throw its pending work away, so that
   * "Leave and lose changes" really does lose it.
   */
  registerAbandon: (abandon: (() => void) | null) => void;
  /**
   * Go somewhere else. When a save did not go through, the person is asked
   * first (scene 12); otherwise it just happens.
   */
  requestLeave: (proceed: () => void) => void;
  /**
   * A screen lends the box its "store now and tell me whether it is kept".
   */
  registerSaveNow: (
    saveNow: (() => Promise<boolean>) | null,
    isKept?: (() => boolean) | null
  ) => void;
  /** A switch is waiting for its save: the screen says so in its corner. */
  switching: boolean;
  /**
   * Go somewhere else WITHOUT leaving the screen behind: picking another
   * campaign. The work is stored first, the ordinary way; only then does it
   * happen. If the work cannot be stored, the same question is asked as for
   * any other way of leaving (Steven, 3 Oct 2026). `onStay` runs when the
   * person chooses to stay, so whatever offered the choice can put itself back
   * (the campaign picker would otherwise go on showing the campaign that was
   * picked but never opened).
   */
  saveThenLeave: (proceed: () => void, onStay?: () => void) => void;
  /**
   * The person picked the place they are already in while a switch was still
   * waiting for its save: that IS the latest pick, and it means "stay here".
   * The waiting switch is dropped. Nothing happens when no switch is waiting.
   */
  stayAfterAll: () => void;
  /**
   * Throws away, deliberately, what a screen holds and has not stored yet.
   * For "Discard changes": that work is part of what is being discarded, and
   * left alone it would be stored a moment after the discard.
   */
  abandonWork: () => void;
  /**
   * Stores what the open screen holds, now, and says whether it is ALL kept.
   * Asks nothing and throws nothing away. For "Publish changes" and "Discard
   * changes": both are refused while work cannot be stored, they never offer
   * to go on without it (5b item 11; review of slice 2, 4 Oct 2026: going
   * through the leave question offered "Leave and lose changes", and that
   * button then published without the words just typed).
   */
  keepNow: () => Promise<boolean>;
  /**
   * Do this as soon as nothing is waiting to be stored any more. For a
   * campaign that was created while the open one could not be saved and the
   * person chose "Stay": it opens once the work is saved (Steven, 4 Oct 2026).
   * One such wish at a time; a newer one replaces it.
   */
  whenKept: (run: () => void) => void;
}

const PendingSaveContext = createContext<PendingSaveContextType>({
  setPending: () => undefined,
  registerAbandon: () => undefined,
  requestLeave: (proceed) => proceed(),
  registerSaveNow: () => undefined,
  switching: false,
  saveThenLeave: (proceed) => proceed(),
  stayAfterAll: () => undefined,
  abandonWork: () => undefined,
  keepNow: () => Promise.resolve(true),
  whenKept: (run) => run(),
});

export const usePendingSave = (): PendingSaveContextType =>
  useContext(PendingSaveContext);

/**
 * Holds the one question the whole app needs to answer while auto-save is on:
 * is there work that the server has not confirmed? The sidebar asks before it
 * takes the person somewhere else.
 */
export const PendingSaveProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const pending = useRef(false);
  const abandonRef = useRef<(() => void) | null>(null);
  const letThroughRef = useRef<HTMLAnchorElement | null>(null);
  const proceedRef = useRef<(() => void) | null>(null);
  const [asking, setAsking] = useState(false);

  const [refusedBecause, setRefusedBecause] = useState<string | null>(null);
  const setPending = useCallback((next: boolean, notStoredBecause?: string) => {
    pending.current = next;
    setRefusedBecause(next && notStoredBecause ? notStoredBecause : null);
  }, []);

  const registerAbandon = useCallback((abandon: (() => void) | null) => {
    abandonRef.current = abandon;
  }, []);

  const requestLeave = useCallback((proceed: () => void) => {
    if (!pending.current) {
      proceed();
      return;
    }
    proceedRef.current = proceed;
    setAsking(true);
  }, []);

  const saveNowRef = useRef<(() => Promise<boolean>) | null>(null);
  const isKeptRef = useRef<(() => boolean) | null>(null);
  const registerSaveNow = useCallback(
    (
      saveNow: (() => Promise<boolean>) | null,
      isKept?: (() => boolean) | null
    ) => {
      saveNowRef.current = saveNow;
      isKeptRef.current = isKept ?? null;
    },
    []
  );
  const [switching, setSwitching] = useState(false);

  /** One switch at a time: a second pick while the first waits is ignored. */
  const leavingRef = useRef(false);
  const onStayRef = useRef<(() => void) | null>(null);
  const latestRef = useRef<{
    proceed: () => void;
    onStay?: () => void;
  } | null>(null);
  const saveThenLeave = useCallback(
    (proceed: () => void, onStay?: () => void) => {
      const saveNow = saveNowRef.current;
      if (!saveNow) {
        requestLeave(proceed);
        return;
      }
      /*
       * The LATEST pick is the one that happens. A second pick while the
       * first is still waiting replaces it: someone who picked the wrong
       * campaign and corrected it at once must not land on the wrong one
       * (review round 8).
       */
      latestRef.current = { proceed, onStay };
      if (leavingRef.current) return;
      leavingRef.current = true;
      setSwitching(true);

      saveNow()
        .then(
          (kept) => (kept ? 'kept' : 'not kept'),
          (error) => (error instanceof SaveNowMoot ? 'moot' : 'not kept')
        )
        .then((outcome) => {
          leavingRef.current = false;
          setSwitching(false);
          const latest = latestRef.current;
          latestRef.current = null;
          if (!latest) return;

          if (outcome === 'kept') {
            latest.proceed();
            return;
          }
          // The person chose "Leave and lose changes" for something else
          // while this waited, or a question for something else is on screen:
          // this switch is dropped, and the picker put back.
          if (outcome === 'moot' || proceedRef.current) {
            latest.onStay?.();
            return;
          }
          proceedRef.current = latest.proceed;
          onStayRef.current = latest.onStay ?? null;
          setAsking(true);
        });
    },
    [requestLeave]
  );

  const saveThenLeaveRef = useRef(saveThenLeave);
  saveThenLeaveRef.current = saveThenLeave;

  /*
   * The question is taken back when the work lands after all, whatever raised
   * it: a campaign switch or a sidebar link (round 9: it was first built for
   * the switch only). The hook keeps
   * retrying behind it; once everything is kept there is nothing left to
   * lose, so the question closes and the switch goes ahead. Leaving it up
   * invited "Leave and lose changes" for work that was already safe, and
   * counted that press in the figure the feature is measured by (review
   * round 7, G3).
   */
  useEffect(() => {
    if (!asking) return;
    const watch = window.setInterval(() => {
      if (!isKeptRef.current?.()) return;
      const proceed = proceedRef.current;
      proceedRef.current = null;
      onStayRef.current = null;
      setAsking(false);
      proceed?.();
    }, 300);
    return () => window.clearInterval(watch);
  }, [asking]);

  const stayAfterAll = useCallback(() => {
    if (!leavingRef.current) return;
    const dropped = latestRef.current;
    latestRef.current = { proceed: () => undefined };
    dropped?.onStay?.();
  }, []);

  const abandonWork = useCallback(() => {
    pending.current = false;
    abandonRef.current?.();
  }, []);

  const keepNow = useCallback((): Promise<boolean> => {
    const saveNow = saveNowRef.current;
    if (!saveNow) return Promise.resolve(!pending.current);
    return saveNow().then(
      (kept) => kept && (isKeptRef.current?.() ?? true),
      () => false
    );
  }, []);

  const wishRef = useRef<(() => void) | null>(null);
  const wishTimer = useRef<number | undefined>(undefined);
  const whenKept = useCallback((run: () => void) => {
    wishRef.current = run;
    window.clearInterval(wishTimer.current);
    wishTimer.current = window.setInterval(() => {
      const allKept = !pending.current && (isKeptRef.current?.() ?? true);
      if (!allKept) return;
      window.clearInterval(wishTimer.current);
      const wish = wishRef.current;
      wishRef.current = null;
      wish?.();
    }, 300);
  }, []);
  useEffect(() => () => window.clearInterval(wishTimer.current), []);

  const stay = () => {
    proceedRef.current = null;
    setAsking(false);
    const onStay = onStayRef.current;
    onStayRef.current = null;
    onStay?.();
  };

  const refusedBecauseRef = useRef<string | null>(null);
  refusedBecauseRef.current = refusedBecause;
  const leaveAndLose = () => {
    const proceed = proceedRef.current;
    proceedRef.current = null;
    onStayRef.current = null;
    pending.current = false;
    setAsking(false);
    // First throw the work away, THEN go. The other order sends it.
    abandonRef.current?.();
    // 5b item 24 and "solves the problem" point 1: the CHOICE is counted, never
    // what was typed. This is the number the whole epic is measured by.
    // "Leave and go back" (a screen that was refused, D35) is counted apart.
    fireEvent(
      refusedBecauseRef.current
        ? 'autosave_leave_and_go_back'
        : 'autosave_leave_and_lose_changes'
    );
    proceed?.();
  };

  /**
   * Every screen in the app is reached through a real link, so one listener in
   * front of them all catches "she clicks Targeting" (scene 12) without each
   * screen having to remember to ask.
   */
  useEffect(() => {
    const onClickAnywhere = (event: MouseEvent) => {
      /*
       * Work that is still on its way (inside the pause, or in the air) is
       * stored FIRST, and the click goes through when the answer is in. It
       * used to be sent as the screen closed, with nobody reading the
       * answer: on a screen that was out of date the server refused it and
       * the person believed it was saved (review of slice 4).
       */
      const onItsWay =
        !pending.current &&
        !!saveNowRef.current &&
        !(isKeptRef.current?.() ?? true);
      if (!pending.current && !onItsWay) return;
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey) return;

      const anchor = (event.target as HTMLElement | null)?.closest?.(
        'a[href]'
      ) as HTMLAnchorElement | null;
      if (!anchor) return;
      // The click we are replaying after "Leave and lose changes".
      if (letThroughRef.current === anchor) return;

      const target = new URL(anchor.href, window.location.href);
      // Leaving sympl altogether is the browser's own prompt, not ours.
      if (target.origin !== window.location.origin) return;
      if (target.pathname === window.location.pathname) return;

      /*
       * The WHOLE click is held back, not just the navigation.
       *
       * The sidebar does its own work in a React handler on the same click, and
       * for the Logout row that work IS the logout: letting it through would
       * end the session behind the question, so "Stay" would leave the person
       * on a dead session. So the click is stopped here and, if they choose to
       * go, the very same click is let through afterwards -- which runs the
       * sidebar's work and the navigation in their normal order.
       */
      event.preventDefault();
      event.stopPropagation();

      const go = () => {
        letThroughRef.current = anchor;
        anchor.click();
        letThroughRef.current = null;
      };

      if (onItsWay) {
        // Kept: go. Not kept: the question. Refused (out of date): stay; the
        // screen's own box is up.
        saveThenLeaveRef.current(go);
        return;
      }

      proceedRef.current = go;
      setAsking(true);
    };

    document.addEventListener('click', onClickAnywhere, true);
    return () => document.removeEventListener('click', onClickAnywhere, true);
  }, [navigate]);

  return (
    <PendingSaveContext.Provider
      value={{
        setPending,
        registerAbandon,
        requestLeave,
        registerSaveNow,
        saveThenLeave,
        stayAfterAll,
        abandonWork,
        keepNow,
        whenKept,
        switching,
      }}
    >
      {children}
      <Modal show={asking} onClose={stay}>
        <div
          tw="flex flex-col gap-4 w-[32rem] max-w-[80vw] pr-10"
          data-testid="leave-warning"
        >
          <Title3>
            {refusedBecause
              ? `This screen is not saved: ${refusedBecause}. If you leave now, it goes back to how it was last saved.`
              : 'Your last changes are not saved yet.'}
          </Title3>
          <div tw="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={stay}
              testId="leave-warning-stay"
            >
              Stay
            </Button>
            <Button onClick={leaveAndLose} testId="leave-warning-leave">
              {refusedBecause ? 'Leave and go back' : 'Leave and lose changes'}
            </Button>
          </div>
        </div>
      </Modal>
    </PendingSaveContext.Provider>
  );
};
